/* KrutiKala PDP — gallery, variant picker, quantity, delivery checker, sticky bar */
'use strict';

(function () {
  // ── Gallery ──────────────────────────────────────────────────────────────
  class KKGallery extends HTMLElement {
    connectedCallback() {
      this._imgs  = Array.from(this.querySelectorAll('[data-kk-gallery-img]'));
      this._thumbs = Array.from(this.querySelectorAll('[data-kk-gallery-thumb]'));
      this._dots  = Array.from(this.querySelectorAll('[data-kk-gallery-dot]'));
      this._label = this.querySelector('[data-kk-gallery-label]');
      this._section = this.closest('.shopify-section') || document;
      this._lb    = this._section.querySelector('[data-kk-lightbox]');
      this._lbImg = this._lb?.querySelector('[data-kk-lightbox-img]');
      this._lbCtr = this._lb?.querySelector('[data-kk-lightbox-counter]');
      this._lbThumbs = Array.from(this._lb?.querySelectorAll('[data-kk-lightbox-thumb]') || []);
      this._active = 0;
      this._total  = this._imgs.length;
      this._lbActive = 0;
      this._initLooks();

      this._thumbs.forEach((t, i) => t.addEventListener('click', () => this._go(i)));
      this._dots.forEach((d, i) => d.addEventListener('click', () => this._go(i)));
      [['[data-kk-gallery-prev]', -1], ['[data-kk-gallery-next]', 1]].forEach(([sel, dir]) => {
        this.querySelector(sel)?.addEventListener('click', () => {
          this._go((this._active + dir + this._total) % this._total);
        });
      });

      this.querySelectorAll('[data-kk-variant-look]').forEach((btn) => {
        btn.addEventListener('click', () => {
          if (window.KKVariantPicker) window.KKVariantPicker.pickById(btn.dataset.variantId);
          this.goToMedia(btn.dataset.mediaId);
          this.querySelectorAll('[data-kk-variant-look]').forEach((b) => {
            b.classList.toggle('is-active', b === btn);
          });
        });
      });
      this._lbThumbs.forEach((t) => {
        t.addEventListener('click', () => {
          const idx = parseInt(t.getAttribute('data-index'), 10) || 0;
          this._lbActive = idx;
          this._lbRender(idx);
        });
      });

      const viewport = this.querySelector('[data-kk-gallery-viewport]');
      if (viewport) viewport.addEventListener('click', () => this._openLightbox(this._active));

      this._lb?.querySelectorAll('[data-kk-lightbox-close]').forEach((btn) => {
        btn.addEventListener('click', () => this._closeLightbox());
      });
      this._lb?.querySelector('[data-kk-lightbox-prev]')?.addEventListener('click', () => this._lbNav(-1));
      this._lb?.querySelector('[data-kk-lightbox-next]')?.addEventListener('click', () => this._lbNav(1));

      document.addEventListener('keydown', (e) => {
        if (!this._lb || this._lb.hidden) return;
        if (e.key === 'ArrowLeft') this._lbNav(-1);
        else if (e.key === 'ArrowRight') this._lbNav(1);
        else if (e.key === 'Escape') this._closeLightbox();
      });

      let tx = 0;
      const area = this.querySelector('[data-kk-gallery-viewport]');
      if (area) {
        area.addEventListener('touchstart', (e) => { tx = e.touches[0].clientX; }, { passive: true });
        area.addEventListener('touchend', (e) => {
          const diff = tx - e.changedTouches[0].clientX;
          if (Math.abs(diff) > 40) {
            this._go((this._active + (diff > 0 ? 1 : -1) + this._total) % this._total);
          }
        });
      }

      this._angles = this.querySelector('.kk-pdp-gallery__angles');
      if (this._angles) {
        this._syncAngles = () => {
          const a = this._angles;
          const scrollable = a.scrollHeight > a.clientHeight + 2;
          a.classList.toggle('is-scrollable', scrollable);
          a.classList.toggle('is-end', !scrollable || a.scrollTop + a.clientHeight >= a.scrollHeight - 2);
        };
        this._angles.addEventListener('scroll', this._syncAngles, { passive: true });
        window.addEventListener('resize', this._syncAngles);
        this._syncAngles();
      }

      this._render(0);
    }

    _revealThumb(idx) {
      const a = this._angles;
      const t = this._thumbs[idx];
      if (!a || !t || a.scrollHeight <= a.clientHeight) return;
      const top = t.offsetTop - a.offsetTop;
      if (top < a.scrollTop) a.scrollTo({ top: top - 4, behavior: 'smooth' });
      else if (top + t.offsetHeight > a.scrollTop + a.clientHeight) {
        a.scrollTo({ top: top + t.offsetHeight - a.clientHeight + 8, behavior: 'smooth' });
      }
    }

    _initLooks() {
      this._tints = ['#f4ead8', '#ead6c4', '#dfe8d8', '#e8dcc8', '#d7c4ae', '#f0e2d2'];
      this._materials = [];
      this._stage = this.querySelector('[data-kk-gallery-stage]');
      this._orbits = Array.from(this.querySelectorAll('[data-kk-orbit-img]'));
      const node = this.querySelector('[data-kk-gallery-looks]');
      if (!node) return;
      try {
        const data = JSON.parse(node.textContent);
        if (Array.isArray(data.tints) && data.tints.length) this._tints = data.tints;
        if (Array.isArray(data.materials)) {
          this._materials = data.materials.filter(Boolean);
        }
      } catch (err) {
        this._materials = [];
      }
      this._orbits.forEach((img) => {
        img.addEventListener('error', () => {
          img.style.visibility = 'hidden';
        });
      });
    }

    _updateOrbits(idx) {
      if (this._stage && this._tints.length) {
        this._stage.style.setProperty('--kk-orbit-tint', this._tints[idx % this._tints.length]);
      }
      if (!this._orbits.length || !this._materials.length) return;
      this._orbits.forEach((img, i) => {
        const src = this._materials[(idx + i + 1) % this._materials.length];
        if (!src || img.getAttribute('src') === src) return;
        const wrap = img.closest('.kk-pdp-gallery__orbit');
        wrap?.classList.add('is-swap');
        window.setTimeout(() => {
          img.src = src;
          wrap?.classList.remove('is-swap');
        }, 180);
      });
    }

    _go(idx) {
      if (idx === this._active) {
        this._updateOrbits(idx);
        return;
      }
      this._active = idx;
      this._render(idx);
    }

    goToMedia(mediaId) {
      if (!mediaId) return;
      const idx = this._imgs.findIndex((img) => String(img.dataset.mediaId) === String(mediaId));
      if (idx >= 0) this._go(idx);
    }

    _render(idx) {
      this._imgs.forEach((img, i) => img.classList.toggle('is-active', i === idx));
      this._thumbs.forEach((t, i) => t.classList.toggle('is-active', i === idx));
      this._revealThumb(idx);
      this._dots.forEach((d, i) => d.classList.toggle('is-active', i === idx));
      const src = this._imgs[idx]?.dataset.label;
      if (this._label && src) this._label.textContent = src;
      this._updateOrbits(idx);
    }

    _openLightbox(idx) {
      if (!this._lb) return;
      this._lbActive = idx;
      this._lbRender(idx);
      // Move to body so sticky/transform ancestors cannot trap the overlay
      if (this._lb.parentElement !== document.body) {
        document.body.appendChild(this._lb);
      }
      this._lb.hidden = false;
      document.documentElement.classList.add('kk-lightbox-open');
      document.body.style.overflow = 'hidden';
    }

    _closeLightbox() {
      if (!this._lb) return;
      this._lb.hidden = true;
      document.documentElement.classList.remove('kk-lightbox-open');
      document.body.style.overflow = '';
    }

    _lbNav(dir) {
      this._lbActive = (this._lbActive + dir + this._total) % this._total;
      this._lbRender(this._lbActive);
    }

    _lbRender(idx) {
      const img = this._imgs[idx];
      if (!img || !this._lbImg) return;
      this._lbImg.src = img.getAttribute('data-full') || img.src;
      this._lbImg.alt = img.alt;
      if (this._lbCtr) this._lbCtr.textContent = `${idx + 1} / ${this._total}`;
      this._lbThumbs.forEach((t, i) => t.classList.toggle('is-active', i === idx));
    }
  }
  customElements.define('kk-gallery', KKGallery);

  // ── Variant picker (option buttons + image looks) ─────────────────────────
  window.KKVariantPicker = {
    _variants: [],
    init: function () {
      const node = document.querySelector('[data-kk-variant-json]');
      if (!node) return;
      try {
        this._variants = JSON.parse(node.textContent);
      } catch (err) {
        this._variants = [];
      }
    },
    currentOptions: function () {
      const selected = [];
      document.querySelectorAll('.kk-pdp-options').forEach((group) => {
        const active = group.querySelector('.kk-pdp-option-btn.is-active, .kk-pdp-option-btn[aria-checked="true"]');
        selected.push(active ? active.dataset.optionValue : '');
      });
      return selected;
    },
    find: function (options) {
      return this._variants.find((v) =>
        options.every((value, i) => String(v.options[i]) === String(value))
      ) || null;
    },
    pick: function (btn) {
      if (!btn) return;
      const group = btn.closest('.kk-pdp-options');
      if (group) {
        group.querySelectorAll('.kk-pdp-option-btn').forEach((el) => {
          const on = el === btn;
          el.classList.toggle('is-active', on);
          el.setAttribute('aria-checked', on ? 'true' : 'false');
        });
      }
      const variant = this.find(this.currentOptions());
      if (variant) this.apply(variant);
    },
    pickById: function (id) {
      const variant = this._variants.find((v) => String(v.id) === String(id));
      if (!variant) return;
      (variant.options || []).forEach((value, index) => {
        const group = document.querySelectorAll('.kk-pdp-options')[index];
        if (!group) return;
        group.querySelectorAll('.kk-pdp-option-btn').forEach((el) => {
          const on = el.dataset.optionValue === String(value);
          el.classList.toggle('is-active', on);
          el.setAttribute('aria-checked', on ? 'true' : 'false');
        });
      });
      this.apply(variant);
    },
    apply: function (variant) {
      document.querySelectorAll('input[name="id"]').forEach((input) => {
        input.value = variant.id;
      });

      const price = document.querySelector('.kk-pdp-price__current');
      if (price) price.textContent = variant.price_money;

      const compare = document.querySelector('.kk-pdp-price__compare');
      const badge = document.querySelector('.kk-pdp-price__badge');
      if (variant.compare_money) {
        if (compare) {
          compare.hidden = false;
          compare.textContent = variant.compare_money;
        }
        if (badge) {
          const pct = variant.compare > variant.price
            ? Math.round(((variant.compare - variant.price) * 100) / variant.compare)
            : 0;
          badge.hidden = false;
          badge.textContent = pct + '% OFF';
        }
      } else {
        if (compare) compare.hidden = true;
        if (badge) badge.hidden = true;
      }

      document.querySelectorAll('.kk-pdp-ctas__add, .kk-pdp-gallery__cart-btn').forEach((addBtn) => {
        if (addBtn.tagName === 'A') return;
        addBtn.disabled = !variant.available && !(window.KKCart && window.KKCart.hasVariant(variant.id));
        addBtn.setAttribute('data-kk-add-to-cart', String(variant.id));
        if (!variant.available && addBtn.getAttribute('data-kk-in-cart') !== 'true') {
          const label = addBtn.querySelector('[data-kk-add-label], span');
          if (label) label.textContent = 'Sold out';
          else addBtn.textContent = 'Sold out';
        }
      });
      if (window.KKCart && typeof window.KKCart.syncButtons === 'function') {
        window.KKCart.syncButtons();
      }

      const qty = document.querySelector('[data-kk-qty-input]');
      const qtyVal = qty ? qty.value : '1';
      document.querySelectorAll('[data-kk-buy-now]').forEach((link) => {
        if (!variant.available) {
          link.setAttribute('aria-disabled', 'true');
        } else {
          link.removeAttribute('aria-disabled');
        }
        link.href = '/cart/' + variant.id + ':' + qtyVal;
      });

      document.querySelectorAll('[data-kk-wishlist]').forEach((wish) => {
        wish.dataset.variantId = String(variant.id);
        wish.dataset.available = variant.available ? 'true' : 'false';
        wish.dataset.productPrice = String(variant.price);
        wish.dataset.productCompare = String(variant.compare || 0);
      });

      document.querySelectorAll('[data-kk-variant-look]').forEach((look) => {
        look.classList.toggle('is-active', look.dataset.variantId === String(variant.id));
      });

      const gallery = document.querySelector('kk-gallery');
      if (gallery && variant.mediaId && typeof gallery.goToMedia === 'function') {
        gallery.goToMedia(variant.mediaId);
      }
    },
  };
  window.KKVariantPicker.init();

  // ── Quantity stepper ─────────────────────────────────────────────────────
  class KKQty extends HTMLElement {
    connectedCallback() {
      this._input = this.querySelector('[data-kk-qty-input]');
      this._display = this.querySelector('.kk-pdp-qty__value');
      this.querySelector('[data-kk-qty-dec]')?.addEventListener('click', () => this._change(-1));
      this.querySelector('[data-kk-qty-inc]')?.addEventListener('click', () => this._change(1));
      this._sync();
    }
    _change(d) {
      if (!this._input) return;
      const v = parseInt(this._input.value, 10) || 1;
      const min = parseInt(this._input.min, 10) || 1;
      const max = parseInt(this._input.max, 10) || 99;
      this._input.value = String(Math.min(max, Math.max(min, v + d)));
      this._sync();
    }
    _sync() {
      if (!this._input) return;
      const value = parseInt(this._input.value, 10) || 1;
      const min = parseInt(this._input.min, 10) || 1;
      const max = parseInt(this._input.max, 10) || 99;
      if (this._display) this._display.textContent = String(value);
      const dec = this.querySelector('[data-kk-qty-dec]');
      const inc = this.querySelector('[data-kk-qty-inc]');
      if (dec) dec.disabled = value <= min;
      if (inc) inc.disabled = value >= max;

      const form = this.closest('form');
      const variantInput = form?.querySelector('input[name="id"]');
      const variantId = variantInput?.value;
      if (variantId) {
        document.querySelectorAll('[data-kk-buy-now]').forEach((link) => {
          link.href = `/cart/${variantId}:${value}`;
        });
      }
    }
  }
  customElements.define('kk-qty', KKQty);

  // ── Delivery checker ──────────────────────────────────────────────────────
  class KKDelivery extends HTMLElement {
    connectedCallback() {
      this._input  = this.querySelector('[data-kk-pin-input]');
      this._result = this.querySelector('[data-kk-pin-result]');
      this.querySelector('[data-kk-pin-btn]')?.addEventListener('click', () => this._check());
    }
    _check() {
      const pin = (this._input?.value || '').trim();
      if (!/^\d{6}$/.test(pin)) {
        this._result.textContent = 'Please enter a valid 6-digit PIN code.';
        this._result.className = 'kk-pdp-delivery__result kk-pdp-delivery__result--err';
        return;
      }
      const days = Math.floor(Math.random() * 4) + 5;
      const d1 = new Date(); d1.setDate(d1.getDate() + days);
      const d2 = new Date(d1); d2.setDate(d2.getDate() + 3);
      const fmt = (d) => d.toLocaleDateString('en-IN', { day: 'numeric', month: 'long' });
      this._result.innerHTML = `Delivery available — <strong>${fmt(d1)} – ${fmt(d2)}</strong>. Free shipping · COD available.`;
      this._result.className = 'kk-pdp-delivery__result kk-pdp-delivery__result--ok';
    }
  }
  customElements.define('kk-delivery', KKDelivery);

  // ── Tab navigation (smooth scroll) ───────────────────────────────────────
  document.querySelectorAll('[data-kk-tab-btn]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const target = document.getElementById(btn.dataset.kkTabBtn);
      if (!target) return;
      const offset = 100;
      const top = target.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
      document.querySelectorAll('[data-kk-tab-btn]').forEach((b) => b.setAttribute('aria-selected', 'false'));
      btn.setAttribute('aria-selected', 'true');
    });
  });

  // Auto-highlight tab on scroll
  const panels = Array.from(document.querySelectorAll('[data-kk-tab-panel]'));
  const tabBtns = Array.from(document.querySelectorAll('[data-kk-tab-btn]'));
  if (panels.length && tabBtns.length) {
    const panelObs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const id = e.target.id;
            tabBtns.forEach((b) => b.setAttribute('aria-selected', b.dataset.kkTabBtn === id ? 'true' : 'false'));
          }
        });
      },
      { threshold: 0.3, rootMargin: '-100px 0px -60% 0px' }
    );
    panels.forEach((p) => panelObs.observe(p));
  }

  // ── FAQ accordion ─────────────────────────────────────────────────────────
  document.querySelectorAll('.kk-faq-item').forEach((item) => {
    const btn = item.querySelector('.kk-faq-item__btn');
    const body = item.querySelector('.kk-faq-item__body');
    if (!btn || !body) return;
    const startOpen = item.classList.contains('open') || btn.getAttribute('aria-expanded') === 'true';
    body.hidden = !startOpen;
    btn.setAttribute('aria-expanded', startOpen ? 'true' : 'false');
    item.classList.toggle('open', startOpen);
    btn.addEventListener('click', () => {
      const open = btn.getAttribute('aria-expanded') === 'true';
      // Close siblings in the same list for accordion behavior
      const list = item.parentElement;
      if (list) {
        list.querySelectorAll('.kk-faq-item').forEach((sib) => {
          if (sib === item) return;
          const sBtn = sib.querySelector('.kk-faq-item__btn');
          const sBody = sib.querySelector('.kk-faq-item__body');
          if (sBtn) sBtn.setAttribute('aria-expanded', 'false');
          if (sBody) sBody.hidden = true;
          sib.classList.remove('open');
        });
      }
      btn.setAttribute('aria-expanded', open ? 'false' : 'true');
      body.hidden = open;
      item.classList.toggle('open', !open);
    });
  });

  // ── Wishlist toggle (handled by kk-wishlist.js) ───────────────────────────

})();
